import type { WardrobeModule } from "../types";

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export const SHELVING_COLOR_PRESETS = [
  { id: "soft-white", name: "Мягкий белый", hex: "#F2F0E9" },
  { id: "sand", name: "Песочный", hex: "#D8C5A6" },
  { id: "beige", name: "Бежевый", hex: "#CDBCA7" },
  { id: "stone", name: "Каменно-серый", hex: "#AAA9A2" },
  { id: "graphite", name: "Графит", hex: "#3C3E3D" },
  { id: "black", name: "Чёрный", hex: "#171918" },
  { id: "butter", name: "Сливочно-жёлтый", hex: "#E7C85F" },
  { id: "terracotta", name: "Терракотовый", hex: "#B76549" },
  { id: "dusty-pink", name: "Пыльно-розовый", hex: "#C99CA0" },
  { id: "burgundy", name: "Бордовый", hex: "#713B45" },
  { id: "sky", name: "Небесно-голубой", hex: "#82AFC1" },
  { id: "midnight", name: "Полуночный синий", hex: "#263C55" },
  { id: "sage", name: "Шалфей", hex: "#8EA38D" },
  { id: "moss", name: "Мох", hex: "#526B4F" }
] as const;

export const DEFAULT_SHELVING_COLOR_HEX = SHELVING_COLOR_PRESETS[0].hex;

export const normalizeShelvingColorHex = (value: unknown) => {
  const text = String(value ?? "").trim();
  return HEX_COLOR.test(text) ? text.toUpperCase() : undefined;
};

export const normalizeShelvingColorCode = (value: unknown) => {
  const text = String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 32);
  return text || undefined;
};

export interface EnamelFinish {
  hex: string;
  code?: string;
}

/** Цвет эмали словами: код NCS или «цвет по палитре #…». */
export const enamelColorLabel = (finish: EnamelFinish) => finish.code ?? `цвет по палитре ${finish.hex}`;

/** Корпус стеллажа или комода в эмали (28.10); null — корпус из ЛДСП. */
export const shelvingCarcassEnamel = (
  module: Pick<WardrobeModule, "type" | "carcassEnamelHex" | "carcassEnamelCode">
): EnamelFinish | null => {
  const hex = module.type === "shelving" ? normalizeShelvingColorHex(module.carcassEnamelHex) : undefined;
  if (!hex) return null;
  const code = normalizeShelvingColorCode(module.carcassEnamelCode);
  return code ? { hex, code } : { hex };
};

/** Столешница комода в своей эмали (28.10); null — как корпус или свой декор. */
export const chestTopEnamel = (
  module: Pick<WardrobeModule, "type" | "shelvingKind" | "stackedOnId" | "topEnamelHex" | "topEnamelCode">
): EnamelFinish | null => {
  if (module.type !== "shelving" || module.shelvingKind !== "chest" || module.stackedOnId) return null;
  const hex = normalizeShelvingColorHex(module.topEnamelHex);
  if (!hex) return null;
  const code = normalizeShelvingColorCode(module.topEnamelCode);
  return code ? { hex, code } : { hex };
};

/** Цвет корпуса в 3D: эмаль или однотонная отделка готовой модели; нет — декор ЛДСП. */
export const shelvingCarcassColorHex = (module: WardrobeModule) =>
  shelvingCarcassEnamel(module)?.hex ?? normalizeShelvingColorHex(module.shelvingColorHex);

export const shelvingFinishLabel = (
  module: Pick<WardrobeModule, "type" | "shelvingColorHex" | "shelvingColorCode" | "carcassEnamelHex" | "carcassEnamelCode">
) => {
  const enamel = shelvingCarcassEnamel(module);
  if (enamel) return `корпус — МДФ в эмали, ${enamelColorLabel(enamel)}`;
  const color = normalizeShelvingColorHex(module.shelvingColorHex);
  if (!color) return undefined;
  const code = normalizeShelvingColorCode(module.shelvingColorCode);
  return `однотонная отделка ${code ?? color}`;
};
